import { useEffect, useMemo, useState } from 'react';
import { TierBox } from '@/components/TierBox';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useCompanies, type Product } from '@/api/insights';
import { PageHeader } from '@/components/PageHeader';
import { DesktopOnly } from '@/components/dashboard/DesktopOnly';
import { SearchInput } from '@/components/ui/search-input';
import { ROUTES, companyPath } from '@/lib/routes';
import { cn } from '@/lib/utils';

/**
 * Companies — every Autotask company that appears in at least one module, with where it's linked
 * (Datto RMM, Microsoft 365, CSP, SaaS backup), plus "mapping health": records in each product
 * that aren't linked to any company, with where to fix them.
 */

const PRODUCTS: Product[] = ['rmm', 'cipp', 'csp', 'backup', 'network', 'domains', 'docs', 'soc'];
const SHORT: Record<Product, string> = { rmm: 'RMM', cipp: 'M365', csp: 'CSP', backup: 'Backup', network: 'Network', domains: 'Domains', docs: 'Docs', soc: 'SOC' };
// Everything is fixed from one place now (Admin → Client mappings); Datto RMM and IT Glue links
// are shown there read-only with a "Fix in …" link, since their own Autotask integrations own them.
const FIX: Record<Product, { to: string; label: string }> = {
  rmm: { to: ROUTES.admin.mappings, label: 'Client mappings (fix in Datto RMM)' },
  cipp: { to: ROUTES.admin.mappings, label: 'Client mappings' },
  csp: { to: ROUTES.admin.mappings, label: 'Client mappings' },
  backup: { to: ROUTES.admin.mappings, label: 'Client mappings' },
  network: { to: ROUTES.admin.mappings, label: 'Client mappings' },
  domains: { to: ROUTES.admin.mappings, label: 'Client mappings' },
  docs: { to: ROUTES.admin.mappings, label: 'Client mappings (fix in IT Glue)' },
  soc: { to: ROUTES.admin.mappings, label: 'Client mappings' },
};

type Show = 'companies' | 'unmapped';

export default function CompaniesPage() {
  const { data, isLoading, error, isFetching } = useCompanies();
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [show, setShow] = useState<Show>('companies');
  const [q, setQ] = useState('');

  useEffect(() => {
    if (!params.has('show')) return;
    setShow(params.get('show') === 'unmapped' ? 'unmapped' : 'companies');
    setParams({}, { replace: true });
  }, [params, setParams]);

  const rows = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (data?.companies ?? []).filter((c) => !s || c.name.toLowerCase().includes(s));
  }, [data, q]);

  const avail = PRODUCTS.filter((p) => data?.available[p]);

  return (
    <DesktopOnly title="Companies is available on desktop" message="Open Cosmos on a larger screen to browse companies.">
      <div className="mx-auto w-full max-w-[1400px] px-4 py-5 md:px-6">
        <PageHeader title="Companies" count={data ? (show === 'companies' ? rows.length : data.unmapped.length) : null} isFetching={isFetching}
          subtitle="Every client across Cosmos and where it's linked. Click a company for its Orbit."
          actions={show === 'companies' && <SearchInput withIcon placeholder="Company" className="w-64" value={q} onValueChange={setQ} />} />

        <div className="mt-3 flex gap-1 rounded-lg bg-muted p-1" style={{ width: 'fit-content' }}>
          {([['companies', 'Companies', data?.companies.length], ['unmapped', 'Mapping health', data?.unmapped.length]] as [Show, string, number | undefined][]).map(([id, label, n]) => (
            <button key={id} type="button" onClick={() => setShow(id)}
              className={cn('rounded-md px-3 py-1 text-xs font-medium', show === id ? 'bg-background text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground')}>
              {label} <span className={cn('ml-1 tabular-nums', id === 'unmapped' && n ? 'text-warning' : 'text-muted-foreground')}>{n ?? ''}</span>
            </button>
          ))}
        </div>

        {isLoading ? (
          <div className="flex items-center gap-2 py-16 text-sm text-muted-foreground"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        ) : error ? (
          <div className="mt-4 text-sm text-destructive">Failed to load: {error instanceof Error ? error.message : 'Unknown error'}</div>
        ) : data && show === 'companies' ? (
          <div className="mt-3 overflow-x-auto rounded-lg border border-border bg-card shadow-sm">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-left text-xs uppercase tracking-wide text-muted-foreground">
                <tr>
                  <th className="px-4 py-2 font-medium">Company</th>
                  {avail.map((p) => <th key={p} className="px-3 py-2 text-center font-medium">{SHORT[p]}</th>)}
                  <th className="px-3 py-2 text-right font-medium">RMM devices</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-divider">
                {rows.map((c) => (
                  <tr key={c.id} className="cursor-pointer hover:bg-muted/30" onClick={() => navigate(companyPath(c.id))}>
                    <td className="px-4 py-2 font-medium text-foreground"><span className="flex items-center gap-2"><span className="inline-flex w-[18px] shrink-0 justify-center"><TierBox tier={c.tier} /></span>{c.name}</span></td>
                    {avail.map((p) => (
                      <td key={p} className="px-3 py-2 text-center">
                        {c[p] ? <span className="inline-block h-2 w-2 rounded-full bg-success" title={`Linked in ${data.productLabels[p]}`} />
                          : <span className="text-xs text-muted-foreground" title={`Not in ${data.productLabels[p]}`}>—</span>}
                      </td>
                    ))}
                    <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">{c.rmm?.devices ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : data ? (
          <div className="mt-3 space-y-4">
            <p className="text-xs text-muted-foreground">Records in each product that aren't linked to an Autotask company — they won't appear on Orbit, coverage or reports until they are.</p>
            {avail.map((p) => {
              const items = data.unmapped.filter((u) => u.product === p);
              return (
                <section key={p} className="rounded-lg border border-border bg-card shadow-sm">
                  <div className="flex items-center justify-between gap-3 border-b border-divider px-4 py-2.5">
                    <h2 className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{data.productLabels[p]} · {items.length} unmapped</h2>
                    <Link to={FIX[p].to} className="text-xs text-muted-foreground hover:text-foreground hover:underline">{FIX[p].label} →</Link>
                  </div>
                  {items.length === 0 ? <div className="px-4 py-4 text-xs text-success">Everything is linked.</div> : (
                    <ul className="divide-y divide-divider text-sm">
                      {items.map((u) => (
                        <li key={u.name} className="flex items-center justify-between gap-3 px-4 py-2">
                          <span className="font-medium text-foreground">{u.name}</span>
                          <span className="text-xs text-muted-foreground">{u.detail}</span>
                        </li>
                      ))}
                    </ul>
                  )}
                </section>
              );
            })}
          </div>
        ) : null}
      </div>
    </DesktopOnly>
  );
}
